<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { Component } from 'vue';
import {
  CircleCheckBig,
  Home,
  Info,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';
import { useContentStore } from '@/stores/content';
import { normalizeCmsPlainText } from '@/utils/html';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon =
  (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileTextIcon;

const PAGE_LINK_CARD_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  home: Home,
  info: Info,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon ?? FileCheck,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
};

function iconForName(iconName: string): Component | null {
  return PAGE_LINK_CARD_ICON_MAP[iconName] ?? null;
}

const props = defineProps<{
  page?: number | null;
  description?: string | null;
  /** Block-picker iframe preview (no pages API); takes precedence over `page` + store. */
  preview_title?: string | null;
  preview_description?: string | null;
  preview_icon?: string | null;
}>();

const contentStore = useContentStore();

const isStaticPreview = computed(() => Boolean(props.preview_title?.trim()));

const pageItem = computed(() =>
  props.page != null ? contentStore.getPageById(props.page) : null
);

const url = computed(() =>
  props.page != null ? contentStore.getUrlForPageId(props.page) : '/'
);

const displayDescription = computed(() => {
  const blockDesc = normalizeCmsPlainText(props.description).trim();
  if (blockDesc) return blockDesc;
  return normalizeCmsPlainText(pageItem.value?.meta?.search_description).trim();
});

const staticPreviewDescription = computed(() => {
  const p = props.preview_description?.trim();
  if (p) return p;
  return props.description?.trim() ?? '';
});

const iconComponent = computed(() => {
  const name = pageItem.value?.meta?.icon?.trim();
  if (name && iconForName(name)) return iconForName(name);
  return FileTextIcon;
});

const staticIconComponent = computed(() => {
  const name = props.preview_icon?.trim();
  if (name && iconForName(name)) return iconForName(name);
  return FileTextIcon;
});
</script>

<template>
  <div v-if="isStaticPreview" class="page-link-card page-link-card--preview" role="region">
    LASJBFLASJBF
    <div class="page-link-card__inner">
      <div class="page-link-card__icon-wrap">
        <component
          :is="staticIconComponent"
          class="page-link-card__icon"
          aria-hidden="true"
        />
      </div>
      <div class="page-link-card__text">
        <h3 class="page-link-card__title">{{ (preview_title ?? "").trim() }}</h3>
        <p v-if="staticPreviewDescription" class="page-link-card__description">
          {{ staticPreviewDescription }}
        </p>
      </div>
    </div>
  </div>
  <RouterLink
    v-else-if="page != null && pageItem"
    :to="url"
    class="page-link-card"
  >
    <div class="page-link-card__inner">
      <div class="page-link-card__icon-wrap">
        <component :is="iconComponent" class="page-link-card__icon" aria-hidden="true" />
      </div>
      <div class="page-link-card__text">
        <h3 class="page-link-card__title">{{ pageItem.title }}</h3>
        <p v-if="displayDescription" class="page-link-card__description">{{ displayDescription }}</p>
      </div>
    </div>
  </RouterLink>
</template>

<style scoped>
.page-link-card--preview {
  cursor: default;
}

.page-link-card {
  display: block;
  background-color: #ffffff;
  /* Prototype theme: --radius-lg (rounded-lg on cards) */
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
  text-align: left;
  text-decoration: none;
  color: inherit;
  margin-bottom: 0.75rem;
}
.page-link-card:last-child {
  margin-bottom: 0;
}
.page-link-card:hover {
  border-color: rgba(4, 120, 87, 0.5);
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  transform: translateY(-4px);
}

.page-link-card__inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.page-link-card__icon-wrap {
  width: 3rem;
  height: 3rem;
  /* Prototype: rounded-lg → --radius-lg */
  border-radius: 6px;
  background-color: rgba(4, 120, 87, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}
.page-link-card:hover .page-link-card__icon-wrap {
  background-color: rgba(4, 120, 87, 0.2);
}

.page-link-card__icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #047857;
}

.page-link-card__text {
  min-width: 0;
}

.page-link-card__title {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  transition: color 0.3s ease;
}
.page-link-card:hover .page-link-card__title {
  color: #047857;
}

.page-link-card__description {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: #525252;
}

@media (min-width: 640px) {
  .page-link-card {
    padding: 1.5rem;
  }

  .page-link-card__inner {
    gap: 1rem;
  }

  .page-link-card__icon-wrap {
    width: 3.5rem;
    height: 3.5rem;
    /* Prototype: sm:rounded-xl → --radius-xl */
    border-radius: 8px;
  }

  .page-link-card__icon {
    width: 1.75rem;
    height: 1.75rem;
  }

  .page-link-card__title {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }

  .page-link-card__description {
    font-size: 0.875rem;
  }
}
</style>
